import { CardContentSlot, CardFooterSlot, CardHeaderSlot, CardTriggerSlot } from '@magicschool/spellbook/components/Card/slots';
import { useRenderedChildren } from '@magicschool/spellbook/hooks/useRenderedChildren';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, memo } from 'react';

export type CardProps = React.ComponentProps<'div'> &
  VariantProps<typeof cardVariants> & {
    /** When true, renders children inside a Slot so you can use an `<a>` or `<button>` as the card root element. */
    asChild?: boolean;
  };

const cardVariants = cva('flex flex-col gap-4 rounded-card-md p-4', {
  variants: {
    /** Card behavior: `static` for static content display, `interactive` for clickable cards with hover elevation, focus ring, and cursor pointer */
    variant: {
      static: '',
      interactive: [
        'relative',
        'cursor-pointer',
        'focus-visible:ring-2',
        'focus-visible:ring-focus-ring',
        'focus-visible:ring-offset-1',
        'focus-visible:ring-offset-surface-background-0',
        'has-[[data-spellbook-name="card-trigger"]:focus-visible]:ring-2',
        'has-[[data-spellbook-name="card-trigger"]:focus-visible]:ring-focus-ring',
        'has-[[data-spellbook-name="card-trigger"]:focus-visible]:ring-offset-1',
        'has-[[data-spellbook-name="card-trigger"]:focus-visible]:ring-offset-surface-background-0',
        'shadow-card',
        'hover:shadow-card-hover',
      ],
    },
    /** Background theme: `default` for standard card with border, `primary` for colored background with primary text and icon colors, `success` for success/positive feedback background with success text and icon colors */
    color: {
      default: ['bg-surface-background-0', 'border', 'border-border-default'],
      primary: ['bg-primary-muted-background', '[&_svg]:text-primary-icon'],
      success: ['bg-success-muted-background', '[&_svg]:text-success-icon'],
    },
  },
  compoundVariants: [
    {
      variant: 'interactive',
      color: 'default',
      className: ['hover:border-border-hover', 'transition-all'],
    },
    {
      variant: 'interactive',
      color: 'primary',
      className: ['hover:bg-primary-muted-background-hover', 'active:bg-primary-muted-background-active'],
    },
    {
      variant: 'interactive',
      color: 'success',
      className: ['hover:bg-success-muted-background-hover', 'active:bg-success-muted-background-active'],
    },
  ],
  defaultVariants: {
    variant: 'static',
    color: 'default',
  },
});

type CardSlottedContentProps = {
  slots: {
    header?: React.ReactNode;
    content?: React.ReactNode;
    footer?: React.ReactNode;
  };
};

const CardSlottedContent = memo<CardSlottedContentProps>(({ slots }) => (
  <>
    {slots.header}
    {slots.content}
    {slots.footer}
  </>
));

const CardBase = forwardRef<HTMLDivElement, CardProps>(({ asChild, children, className, color, variant, ...props }, forwardedRef) => {
  /**
   * @TODO [SPE-80] Convert Card Component to Use Semantic HTML Elements
   * https://linear.app/magicschool/issue/SPE-80/convert-card-component-to-use-semantic-html-elements
   */
  const slotConfig = {
    header: { Component: CardHeaderSlot },
    content: { Component: CardContentSlot },
    footer: { Component: CardFooterSlot },
  };
  const { slots } = useSlots(children, slotConfig);

  const { renderedChildren } = useRenderedChildren(children, {
    slots: slotConfig,
    renderContent: ({ slots }) => <CardSlottedContent slots={slots} />,
  });

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp ref={forwardedRef} data-spellbook-name="card" className={cn(cardVariants({ color, variant }), className)} {...props}>
      {asChild ? renderedChildren : <CardSlottedContent slots={slots} />}
    </Comp>
  );
});

export const Card = Object.assign(CardBase, {
  Header: CardHeaderSlot,
  Content: CardContentSlot,
  Footer: CardFooterSlot,
  Trigger: CardTriggerSlot,
});

/**
 * @TODO [SPE-8] Fix `displayName` assignment
 * https://linear.app/magicschool/issue/SPE-8/fix-displayname-assignment
 */
Card.displayName = 'Card';
Card.Header.displayName = 'Card.Header';
Card.Content.displayName = 'Card.Content';
Card.Footer.displayName = 'Card.Footer';
Card.Trigger.displayName = 'Card.Trigger';
